import React, { useEffect, useState } from 'react';
import { Platform, View, Text, TouchableOpacity } from 'react-native';
import { Icon } from 'react-native-elements';
import { Picker } from '@react-native-picker/picker';
import Modal from 'react-native-modal';
import { Button, Dialog } from 'react-native-paper';
import { styles } from 'styles/components/customSelect';
import CustomSelectProps, { IdentifierType, EntityType } from 'types/components/CustomSelectProps';
import { useTranslation } from 'react-i18next';

export default ({
  items,
  callback,
  isCommerceEditor,
  identifierType,
  entityType,
  idOrCodesToPickFirst,
}: CustomSelectProps): JSX.Element => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [names, setNames] = useState<string[]>([]);
  const [idOrCodes, setIdOrCodes] = useState<string[]>([]);
  const isIOS = Platform.OS === 'ios';

  const { t } = useTranslation();

  const renderPicker = () => {
    const selectedItemsNames = (selectedItem: any) => {
      const item = items.find((item: any) => {
        if (identifierType === IdentifierType.Code) {
          return item.code === selectedItem;
        }
        return item.id === selectedItem;
      });
      const commerceName = item ? item.name : '';
      if (names.includes(commerceName)) {
        setNames(names.filter((item) => item !== commerceName));
      } else {
        setNames([...names, commerceName]);
      }
    };

    useEffect(() => {
      // Evitamos ejecutar el callback si el componente  si el array es vacío
      // o si el valor es el mismo que ya tiene el padre para evitar bucles
      if (idOrCodes.length > 0 || isCommerceEditor) {
        callback(idOrCodes);
      }
    }, [idOrCodes]);

    useEffect(() => {
      // Forzar selección del primero si es CommerceEditor y no hay nada seleccionado
      if (isCommerceEditor && items.length > 0 && idOrCodes.length === 0) {
        const firstItem = items[0];
        const val = identifierType === IdentifierType.Code ? firstItem.code : firstItem.id;
        setIdOrCodes([val]);
        setNames([firstItem.name]);
        return;
      }

      if (idOrCodesToPickFirst) {
        const found = items.find((item: any) =>
          identifierType === IdentifierType.Code
            ? item.code === idOrCodesToPickFirst
            : item.id === idOrCodesToPickFirst,
        );

        if (found) {
          const val = identifierType === IdentifierType.Code ? found.code : found.id;
          setIdOrCodes([val]);
          setNames([found.name]);
        }
      }
    }, [idOrCodesToPickFirst, items, isCommerceEditor]);

    return (
      <Picker
        selectedValue={
          isCommerceEditor ? (identifierType === IdentifierType.Code ? items[0].code : items[0].id) : names.join(' - ')
        }
        onValueChange={(itemValue) => {
          if (!itemValue) return;
          if (idOrCodes.includes(itemValue)) {
            setIdOrCodes(idOrCodes.filter((item) => item !== itemValue));
          } else {
            setIdOrCodes([...idOrCodes, itemValue]);
          }
          selectedItemsNames(itemValue);
        }}
        style={styles.picker}
      >
        {!isCommerceEditor && (
          <Picker.Item
            label={
              names.length === 0
                ? entityType === EntityType.Venue
                  ? t('select-venue')
                  : t('select-commerce')
                : names.join(' , ')
            }
          />
        )}
        {items
          .slice() // Crea una copia para no mutar el array original
          .sort((a, b) => a.name.localeCompare(b.name, 'es', { sensitivity: 'base' }))
          .map((item, index: number) => (
            <Picker.Item
              key={item.id ?? index} // Es recomendable usar un ID único en lugar del índice
              label={item.name}
              value={identifierType === IdentifierType.Code ? item.code : item.id}
            />
          ))}
      </Picker>
    );
  };

  return (
    <>
      <View>
        {isIOS ? (
          <TouchableOpacity
            style={styles.pickerIOS}
            onPress={() => {
              setModalOpen(true);
            }}
          >
            <Text>{names.join(' - ')}</Text>
            <Icon name="expand-more" color="#3f3a40" />
          </TouchableOpacity>
        ) : (
          renderPicker()
        )}
      </View>

      {isIOS && (
        <Modal
          isVisible={isModalOpen}
          onBackdropPress={() => {
            setModalOpen(false);
          }}
        >
          <View style={styles.modalContainer}>
            {renderPicker()}
            <Dialog.Actions style={{ position: 'absolute', bottom: 15, right: 0 }}>
              <Button
                onPress={() => {
                  setModalOpen(false);
                }}
              >
                {t('cancel')}
              </Button>
              <Button
                onPress={() => {
                  setModalOpen(false);
                }}
              >
                {t('accept')}
              </Button>
            </Dialog.Actions>
          </View>
        </Modal>
      )}
    </>
  );
};
